// shared-library/api/services/funnelWebApi.js
/**
 * 商机漏斗相关API服务
 */
export default function(apiClient) {
  return {
    /**
     * 获取所有商机漏斗数据
     */
    getAllFunnels(requestData) {
      return apiClient.post('/cm/web/funnel/list', requestData)
    },
    /**
     * 获取所有商机变更数据
     */
    getAllFunnelChanges(requestData) {
      return apiClient.post('/cm/web/funnel/listChanges', requestData)
    },
    /**
     * 获取所有商机漏斗数据
     */
    getAllFunnelsOverDue(requestData) {
      return apiClient.post('/cm/web/funnel/listOverDue', requestData)
    },
    /**
     * 获取所有商机漏斗数据
     */
    getAllFunnelsPastDue(requestData) {
      return apiClient.post('/cm/web/funnel/listPastDue', requestData)
    },
    /**
     * STORY-010（2026-10-21）：获取「商机关闭-输单」商机漏斗数据
     * ⚠️ 独立端点，不复用 listPastDue —— 上游在未传 cbmBusinessScheduleParentCode 时
     *    会硬排除 cbs_type_code != 'CBS6'（商机关闭），只能单独请求。
     */
    getAllFunnelsClosedLost(requestData) {
      return apiClient.post('/cm/web/funnel/listClosedLost', requestData)
    },

    /**
     * 推进商机
     * @param {object} pushData - 推进数据
     */
    pushFunnel(pushData) {
      return apiClient.post(`/cm/web/funnel/push`, pushData)
    },

    /**
     * 修改商机
     * @param {object} updateData - 修改数据
     */
    updateFunnel(updateData) {
      return apiClient.post(`/cm/web/funnel/update`, updateData)
    },

    /**
     * 创建商机活动记录
     * @param {object} activityData - 活动数据
     */
    createFunnelActivity(activityData) {
      return apiClient.post(`/cm/web/funnel/createActivity`, activityData)
    },
    
    /**
     * 获取商机详情
     * @param {number} busMgmtId - 商机ID
     */
    fetchFunnelDetail(busMgmtId) {
      return apiClient.post(`/cm/web/funnel/detail`, { busMgmtId })
    },

    // ================================================================
    // STORY-001 微信端新建商机页面（透传商机系统 tp* 接口）
    // ================================================================

    /**
     * 新建商机（商机系统 tpSaveBusMgmt）
     * @param {object} saveData - 商机数据（draftFlag=false 提交；true 暂存）
     */
    saveFunnel(saveData) {
      return apiClient.post(`/cm/web/funnel/save`, saveData)
    },

    /**
     * 获取客户（医院）下拉选项（商机系统 tpGetCrmBusHospOptionList）
     * @param {object} requestData - 预留查询参数
     */
    getHospOptionList(requestData) {
      return apiClient.post(`/cm/web/funnel/hospOptionList`, requestData || {})
    },

    /**
     * 获取负责经销商下拉选项（商机系统 tpGetCrmBusOrgOptionList）
     * @param {object} requestData - 预留查询参数
     */
    getOrgOptionList(requestData) {
      return apiClient.post(`/cm/web/funnel/orgOptionList`, requestData || {})
    },

    /**
     * 获取负责人下拉选项（商机系统 tpGetCrmBusOrgPersonOptionList）
     * @param {object} requestData - 预留查询参数
     */
    getOrgPersonOptionList(requestData) {
      return apiClient.post(`/cm/web/funnel/orgPersonOptionList`, requestData || {})
    },

    /**
     * 获取登录人所属团队（骨科/外科）及其可选项（BUG-024）
     *
     * 对齐 PC 端 LoginController#getInfo 的 data.orgPersonSection：
     *   - orgPersonSection：登录人所属团队；为空表示可同时看多个团队（下拉不锁定）
     *   - orgPersonSectionOptions：可选项清单（由后端下发）
     * @param {object} requestData - 预留查询参数
     */
    getUserOrgPersonSection(requestData) {
      return apiClient.post(`/cm/web/funnel/userOrgPersonSection`, requestData || {})
    },

    /**
     * 获取省份下拉选项（该用户授权省份，来自上游「商机系统」）（BUG-024）
     *
     * ⭐ 授权口径与商机列表一致：下拉里选得到的省份 ⇒ 列表一定查得出数据。
     * @param {object} requestData - 预留查询参数
     */
    getBusProvinceOptionList(requestData) {
      return apiClient.post(`/cm/web/funnel/busProvinceOptionList`, requestData || {})
    },

    /**
     * 按客户名称获取产品分类下拉选项（商机系统 tpGetCrmBusProdOptionList）
     * @param {string} cbmHospName - 客户（医院）名称
     */
    getProdOptionList(cbmHospName) {
      return apiClient.post(`/cm/web/funnel/prodOptionList/${encodeURIComponent(cbmHospName)}`)
    },

    /**
     * 保存商机产品明细行（商机系统 tpSaveCrmBusProd）
     * @param {object} prodData - 产品明细数据（须含 cbpBusinessId）
     */
    saveCrmBusProd(prodData) {
      return apiClient.post(`/cm/web/funnel/saveCrmBusProd`, prodData)
    },

    /**
     * 删除商机产品明细行（商机系统 tpDelCrmBusProd）
     * @param {number} prodId - 产品明细行ID
     * @param {number} busMgmtId - 商机ID
     */
    delCrmBusProd(prodId, busMgmtId) {
      return apiClient.post(`/cm/web/funnel/delCrmBusProd/${prodId}/${busMgmtId}`)
    },

    /**
     * 获取商机产品配置明细（商机系统 tpGetCrmBusProductConfigByBusProdId）
     * @param {number} prodId - 产品明细行ID
     */
    getProdConfigByProdId(prodId) {
      return apiClient.post(`/cm/web/funnel/prodConfig/${prodId}`)
    },

    /**
     * 按字典编码获取单级选项（商机系统 tpListOptionsByCodeForOneLevel）
     * @param {string} optionCode - 字典编码，如 CRM_BUSINESS_LEVEL
     */
    listOptionsByCodeForOneLevel(optionCode) {
      return apiClient.post(`/cm/web/funnel/optionsByCode/${encodeURIComponent(optionCode)}`)
    },

    // ==================== STORY-004（2026-10-13）：微信端「草稿箱」 ====================

    /**
     * 获取商机草稿列表（商机系统 getBusMgmtDraftList）
     * @param {object} requestData - 查询条件（可为空，后端强制按当前用户过滤）
     */
    getBusMgmtDraftList(requestData) {
      return apiClient.post(`/cm/web/funnel/draftList`, requestData || {})
    },

    /**
     * 获取商机草稿详情（商机系统 getBusMgmtDraftInfo）
     * @param {number} busMgmtId - 草稿ID
     */
    getBusMgmtDraftInfo(busMgmtId) {
      return apiClient.post(`/cm/web/funnel/draftInfo/${busMgmtId}`)
    },

    // ==================== STORY-006（2026-10-13）：微信端「商机新建页子信息 tabs」 ====================

    /**
     * 获取商机操作记录（字段级修改日志）
     * @param {number} busMgmtId - 商机ID
     */
    getFunnelModifyLog(busMgmtId) {
      return apiClient.post(`/cm/web/funnel/modifyLog/${busMgmtId}`)
    },

    /**
     * 获取商机跟进记录列表
     * @param {number} busMgmtId - 商机ID
     */
    getFunnelActivityList(busMgmtId) {
      return apiClient.post(`/cm/web/funnel/activityList/${busMgmtId}`)
    },

    /**
     * 获取商机竞争态势列表
     * @param {number} busMgmtId - 商机ID
     */
    getFunnelCompetitivList(busMgmtId) {
      return apiClient.post(`/cm/web/funnel/competitivList/${busMgmtId}`)
    },

    /**
     * 保存商机竞争态势
     * @param {object} competitivData - 竞争态势数据（须含 cbcsBusinessId）
     */
    saveFunnelCompetitiv(competitivData) {
      return apiClient.post(`/cm/web/funnel/saveCompetitiv`, competitivData)
    },

    /**
     * 删除商机竞争态势
     * @param {number} situId - 竞争态势ID
     */
    delFunnelCompetitiv(situId) {
      return apiClient.post(`/cm/web/funnel/delCompetitiv/${situId}`)
    },

    /**
     * 获取医院信息（联系人/设备）
     * @param {string} crmBusHospAliasCode - 医院别名编码
     */
    getFunnelHosp(crmBusHospAliasCode) {
      return apiClient.post(`/cm/web/funnel/hospInfo/${encodeURIComponent(crmBusHospAliasCode)}`)
    },

    /**
     * 保存医院联系人
     * @param {object} contactData - 联系人数据（须含 cbhcHospAliasCode）
     */
    saveFunnelHospContact(contactData) {
      return apiClient.post(`/cm/web/funnel/saveHospContact`, contactData)
    },

    /**
     * 删除医院联系人
     * @param {number} contactId - 联系人ID
     */
    delFunnelHospContact(contactId) {
      return apiClient.post(`/cm/web/funnel/delHospContact/${contactId}`)
    },

    /**
     * 保存医院设备
     * @param {object} equipData - 设备数据（须含 cbheHospAliasCode）
     */
    saveFunnelHospEquip(equipData) {
      return apiClient.post(`/cm/web/funnel/saveHospEquip`, equipData)
    },

    /**
     * 删除医院设备
     * @param {number} equipId - 设备ID
     */
    delFunnelHospEquip(equipId) {
      return apiClient.post(`/cm/web/funnel/delHospEquip/${equipId}`)
    },

    // ==================== STORY-009（2026-10-19）：微信端「推进材料」附件 ====================

    /**
     * 上传推进材料附件（商机系统 CGI uploadScheduleFile）
     *
     * ⚠️ CGI 通道只能传 JSON ⇒ 文件以 base64 传递，
     * 上游解码后复用 PC 端同一套落盘逻辑（common_sys_file）。
     * @param {object} payload - { fileName, fileContent(base64) }
     */
    uploadScheduleFile(payload) {
      return apiClient.post(`/cm/web/funnel/uploadScheduleFile`, payload)
    },

    /**
     * 删除推进材料附件（商机系统 CGI delScheduleFile）
     * @param {number} csfId - common_sys_file.id
     */
    delScheduleFile(csfId) {
      return apiClient.post(`/cm/web/funnel/delScheduleFile`, { csfId })
    },

    /**
     * 下载推进材料附件（商机系统 CGI downloadScheduleFile）
     *
     * ⚠️ 必须用 blob 方式取回（JWT 由 axios 拦截器写入请求头，
     * `<a href>` 不会携带 ⇒ 401），再由页面触发下载。
     * @param {number} csfId - common_sys_file.id
     */
    downloadScheduleFile(csfId) {
      return apiClient.get(`/cm/web/funnel/downloadScheduleFile`, {
        params: { csfId },
        responseType: 'blob'
      })
    }
  }
}